import { getData } from './FetchNodeServices';

export interface TraceabilityStatsResponse {
  success: boolean;
  stats: {
    batchesTracked: number;
    posWithTrace: number;
  };
  recentTraces: Array<{
    type: string;
    batch: string;
    detail: string;
    date: string;
  }>;
}

export interface DownstreamInvoice {
  invoiceId?: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerName: string;
  customerPhone?: string;
  qtySold: number;
  unit?: string;
  rate?: number;
  amount?: number;
}

export interface ProductionUsageLink {
  id: string;
  productionOrderNumber: string;
  outputBatchId?: string;
  outputBatchNumber: string;
  outputItemName: string;
  inputQtyUsed: number;
  outputQtyProduced: number;
  outputUnit: string;
  productionDate: string;
  operatorName: string;
  downstreamInvoices?: DownstreamInvoice[];
}

export interface DirectSaleItem {
  invoiceId?: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerName: string;
  customerPhone?: string;
  qtySold: number;
  unit: string;
  rate: number;
  amount: number;
}

export interface ForwardTraceResponse {
  success: boolean;
  found: boolean;
  message?: string;
  data: {
    batchNumber: string;
    itemName?: string;
    batchType?: string;
    initialQty?: number;
    currentQty?: number;
    status?: string;
    mfgDate?: string | null;
    expiryDate?: string | null;
    warehouseName?: string;
    birth?: {
      grnNumber: string;
      supplierName: string;
      supplierPhone?: string;
      supplierBatchNo?: string;
      receivedDate: string;
    };
    productionUsages?: ProductionUsageLink[];
    directSales?: DirectSaleItem[];
    totalProductionUsed?: number;
    totalDirectSold?: number;
    // Legacy fields
    inputItemName: string;
    inputUnit: string;
    totalQtyUsed: number;
    links: ProductionUsageLink[];
  } | null;
}

export interface BackwardRawMaterial {
  id: string;
  inputBatchNumber: string;
  inputItemName: string;
  inputQtyUsed: number;
  inputUnit: string;
  supplierName: string;
  supplierBatchNo: string;
  grnNumber: string;
  receivedDate: string;
}

export interface BackwardInvoiceBatch {
  batchNumber: string;
  itemName: string;
  qtySold: number;
  unit: string;
  sourceType: 'PRODUCTION' | 'DIRECT_PURCHASE';
  productionOrderNumber?: string;
  productionDate?: string;
  operatorName?: string;
  rawMaterials: BackwardRawMaterial[];
}

export interface BackwardTraceResponse {
  success: boolean;
  found: boolean;
  message?: string;
  isInvoiceSearch?: boolean;
  data: {
    // When searched by invoice
    invoiceNumber?: string;
    invoiceDate?: string;
    customerName?: string;
    customerPhone?: string;
    batches?: BackwardInvoiceBatch[];
    // When searched by batch
    customers?: Array<{
      invoiceNumber: string;
      invoiceDate: string;
      customerName: string;
      customerPhone?: string;
      qtySold: number;
    }>;
    outputBatchNumber: string;
    outputItemName: string;
    outputQtyProduced: number;
    outputUnit: string;
    productionOrderNumber: string;
    productionDate: string;
    operatorName: string;
    links: BackwardRawMaterial[];
  } | null;
}

export interface RecallSimulationResponse {
  success: boolean;
  data: {
    batchNumber: string;
    batchType: string;
    inputItem: string;
    totalUsed: number;
    totalProduced: number;
    impactLevel: 'HIGH' | 'MEDIUM' | 'LOW';
    impactColor: string;
    affectedPOs: Array<{
      poNumber: string;
      product: string;
      outputQty: number;
      outputBatch: string;
      unit: string;
      date: string;
    }>;
    affectedCustomers: Array<{
      invoiceNo: string;
      customer: string;
      phone?: string;
      batchNumber?: string;
      qtySold: number;
      date: string;
    }>;
  };
}

export const getTraceabilityStats = async (): Promise<TraceabilityStatsResponse> => {
  return await getData('api/v1/manufacturing/traceability/stats');
};

export const getBatchSuggestions = async (
  type: 'forward' | 'backward',
  search: string = '',
  warehouseId?: string
): Promise<{ success: boolean; data: string[] }> => {
  return await getData('api/v1/manufacturing/traceability/suggestions', {
    type,
    search,
    warehouse_id: warehouseId && warehouseId !== 'ALL' ? warehouseId : undefined,
  });
};

export const getForwardTrace = async (batchNumber: string): Promise<ForwardTraceResponse> => {
  return await getData('api/v1/manufacturing/traceability/forward', { batch_number: batchNumber });
};

export const getBackwardTrace = async (query: string): Promise<BackwardTraceResponse> => {
  return await getData('api/v1/manufacturing/traceability/backward', { search: query });
};

export const getRecallSimulation = async (
  batchNumber: string,
  batchType: 'raw' | 'finished' = 'raw'
): Promise<RecallSimulationResponse> => {
  return await getData('api/v1/manufacturing/traceability/recall', { batch_number: batchNumber, batch_type: batchType });
};
